import api from './api';
import configuracionService from './configuracionService';
import { getApiUrl } from '../config/appConfig';
import { getAccessToken } from '../utils/sessionAuthStore';

export interface ModuloMasterDto {
  campo: string;
  costoMensual: number | null;
  moneda: string | null;
  tieneLogo: boolean;
  logoVersion: string | null;
}

export interface ModuloCostoUpdate {
  costoMensual: number | null;
  moneda: string | null;
}

export interface ModuloHabilitacionResult {
  campo: string;
  habilitado: boolean;
  motivosCreados: string[];
  motivosEliminados: string[];
  motivosConservados: string[];
}

const SECCION_MODULOS = 'MODULOS';

const normalizeMaster = (raw: Record<string, unknown>): ModuloMasterDto => ({
  campo: String(raw.campo ?? raw.Campo ?? ''),
  costoMensual:
    raw.costoMensual != null || raw.CostoMensual != null
      ? Number(raw.costoMensual ?? raw.CostoMensual)
      : null,
  moneda: (raw.moneda ?? raw.Moneda ?? null) as string | null,
  tieneLogo: Boolean(raw.tieneLogo ?? raw.TieneLogo ?? false),
  logoVersion: (raw.logoVersion ?? raw.LogoVersion ?? null) as string | null,
});

const esValorActivo = (valor: string | null | undefined): boolean =>
  String(valor ?? '').trim() === '1';

const modulosService = {
  getEstados: async (): Promise<Record<string, boolean>> => {
    const page = await configuracionService.getConfiguraciones({
      seccion: SECCION_MODULOS,
      usuario: '',
      pageNumber: 1,
      pageSize: 100,
    });
    const dict: Record<string, boolean> = {};
    for (const item of page.items) {
      if ((item.usuario ?? '') !== '') continue;
      dict[item.campo.toUpperCase()] = esValorActivo(item.valor);
    }
    return dict;
  },

  setHabilitado: async (campo: string, habilitado: boolean): Promise<ModuloHabilitacionResult> => {
    const resp = await api.put(`/api/modulos/${encodeURIComponent(campo)}/habilitado`, {
      habilitado,
    });
    const o = (resp.data ?? {}) as Record<string, unknown>;
    const asList = (v: unknown): string[] =>
      Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean) : [];
    return {
      campo: String(o.campo ?? o.Campo ?? campo).toUpperCase(),
      habilitado: Boolean(o.habilitado ?? o.Habilitado ?? habilitado),
      motivosCreados: asList(o.motivosCreados ?? o.MotivosCreados),
      motivosEliminados: asList(o.motivosEliminados ?? o.MotivosEliminados),
      motivosConservados: asList(o.motivosConservados ?? o.MotivosConservados),
    };
  },

  getCatalogoMaster: async (): Promise<ModuloMasterDto[]> => {
    const resp = await api.get('/api/modulos-catalogo');
    const data = resp.data;
    if (!Array.isArray(data)) return [];
    return data.map((item) => normalizeMaster(item as Record<string, unknown>));
  },

  updateCosto: async (campo: string, update: ModuloCostoUpdate): Promise<ModuloMasterDto> => {
    const resp = await api.put(`/api/modulos-catalogo/${encodeURIComponent(campo)}/costo`, {
      costoMensual: update.costoMensual,
      moneda: update.moneda,
    });
    return normalizeMaster(resp.data as Record<string, unknown>);
  },

  uploadLogo: async (campo: string, file: File): Promise<ModuloMasterDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await api.put(`/api/modulos-catalogo/${encodeURIComponent(campo)}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return normalizeMaster(resp.data as Record<string, unknown>);
  },

  deleteLogo: async (campo: string): Promise<ModuloMasterDto> => {
    const resp = await api.delete(`/api/modulos-catalogo/${encodeURIComponent(campo)}/logo`);
    return normalizeMaster(resp.data as Record<string, unknown>);
  },

  getLogoUrl: (campo: string, version?: string | null): string => {
    const base = getApiUrl().replace(/\/$/, '');
    const qs = version ? `?v=${encodeURIComponent(version)}` : '';
    return `${base}/api/modulos-catalogo/${encodeURIComponent(campo)}/logo${qs}`;
  },

  /** Descarga el SVG con el token de sesión (para usar en &lt;img&gt;). */
  fetchLogoObjectUrl: async (campo: string, version?: string | null): Promise<string | null> => {
    try {
      const url = modulosService.getLogoUrl(campo, version);
      const token = getAccessToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        cache: 'no-store',
      });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },
};

export default modulosService;
